import {
  DeviceResult,
  deviceFail,
  deviceOk,
  isBrowser,
} from "./types";

/**
 * Permisos y suscripción a notificaciones push.
 *
 * El detalle que decide el diseño del onboarding: **en iOS las notificaciones push
 * solo funcionan si el usuario instaló la PWA en la pantalla de inicio** (Safari
 * 16.4+). No hay forma de sortearlo, así que la app tiene que detectarlo y guiar la
 * instalación antes de pedir el permiso (§3.2) — pedirlo y que falle en silencio es
 * la peor de las opciones.
 */

export function isIosDevice(): boolean {
  if (!isBrowser()) return false;
  const ua = navigator.userAgent;
  // iPadOS 13+ se identifica como Mac; se distingue por ser táctil.
  const isIpadOs = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  return /iPad|iPhone|iPod/.test(ua) || isIpadOs;
}

/** La app corre instalada, no dentro del navegador. */
export function isInstalledPwa(): boolean {
  if (!isBrowser()) return false;
  const iosStandalone = (navigator as Navigator & { standalone?: boolean })
    .standalone;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches === true ||
    iosStandalone === true
  );
}

export function isPushSupported(): boolean {
  return (
    isBrowser() &&
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
}

/**
 * `true` cuando el único obstáculo para las push es que la app no está instalada.
 * La interfaz muestra las instrucciones de instalación en vez del botón de permiso.
 */
export function pushRequiresInstall(): boolean {
  return isIosDevice() && !isInstalledPwa();
}

export type NotificationPermissionState =
  | NotificationPermission
  | "unsupported";

export function getNotificationPermission(): NotificationPermissionState {
  if (!isBrowser() || !("Notification" in window)) return "unsupported";
  return Notification.permission;
}

/**
 * El navegador solo muestra el diálogo una vez: si el usuario dice que no, la
 * segunda llamada devuelve `denied` sin preguntar nada. Por eso conviene pedirlo
 * después de explicar para qué sirve, no al abrir la app.
 */
export async function requestNotificationPermission(): Promise<
  DeviceResult<NotificationPermission>
> {
  if (!isBrowser() || !("Notification" in window)) {
    return deviceFail(
      "unsupported",
      "Este navegador no admite notificaciones.",
    );
  }

  if (pushRequiresInstall()) {
    return deviceFail(
      "unsupported",
      "En iPhone hay que agregar la app a la pantalla de inicio para recibir avisos.",
    );
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") {
      return deviceFail("denied", "No se autorizaron las notificaciones.");
    }
    return deviceOk(permission);
  } catch (error) {
    return deviceFail(
      "unavailable",
      error instanceof Error ? error.message : "No se pudo pedir el permiso.",
    );
  }
}

/** La llave VAPID viaja en base64url y el navegador la exige como bytes. */
function decodeVapidKey(base64Url: string) {
  const padding = "=".repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  // El respaldo se crea explícitamente para que el tipo sea `ArrayBuffer` y no
  // `ArrayBufferLike`, que `applicationServerKey` no acepta.
  const output = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i);
  return output;
}

/**
 * Devuelve la suscripción en JSON, que es lo que se guarda en el servidor (§9.6).
 * Si ya existía una, se reutiliza: volver a suscribir invalidaría la anterior y el
 * servidor seguiría empujando a un destino muerto.
 */
export async function subscribeToPush(
  vapidPublicKey: string,
): Promise<DeviceResult<PushSubscriptionJSON>> {
  if (!isPushSupported()) {
    return deviceFail("unsupported", "Este dispositivo no admite avisos push.");
  }
  if (getNotificationPermission() !== "granted") {
    return deviceFail("denied", "Falta autorizar las notificaciones.");
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const existing = await registration.pushManager.getSubscription();
    if (existing) return deviceOk(existing.toJSON());

    const subscription = await registration.pushManager.subscribe({
      // Obligatorio en la web: no se permiten push silenciosas.
      userVisibleOnly: true,
      applicationServerKey: decodeVapidKey(vapidPublicKey),
    });
    return deviceOk(subscription.toJSON());
  } catch (error) {
    return deviceFail(
      "unavailable",
      error instanceof Error
        ? error.message
        : "No se pudo registrar el dispositivo para avisos.",
    );
  }
}

export async function getPushSubscription(): Promise<PushSubscriptionJSON | null> {
  if (!isPushSupported()) return null;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return subscription ? subscription.toJSON() : null;
  } catch {
    return null;
  }
}

type BadgingNavigator = Navigator & {
  setAppBadge?: (count?: number) => Promise<void>;
  clearAppBadge?: () => Promise<void>;
};

/**
 * Numeric badge on the installed app icon (App Badging API). Zero clears it.
 * Best-effort: unsupported browsers and non-installed PWAs simply do nothing.
 */
export async function setAppBadgeCount(count: number): Promise<void> {
  if (!isBrowser()) return;
  const nav = navigator as BadgingNavigator;
  try {
    if (count > 0 && nav.setAppBadge) await nav.setAppBadge(count);
    else if (nav.clearAppBadge) await nav.clearAppBadge();
  } catch {
    // The badge is cosmetic; a rejection must never surface to the user.
  }
}

/**
 * Al cerrar sesión. El registro correspondiente en el servidor se borra aparte: si
 * solo se cancela aquí, quedan suscripciones huérfanas empujando al vacío.
 */
export async function unsubscribeFromPush(): Promise<boolean> {
  if (!isPushSupported()) return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return false;
    return await subscription.unsubscribe();
  } catch {
    return false;
  }
}
